import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { BACKEND_DATE_FORMAT, UI_DATE_FORMAT } from "../../../../consts";

dayjs.extend(isBetween);
dayjs.extend(customParseFormat);

function evaluateSingleRule(rule, context) {
  // 1) If there is a timeWindow, check that logic first
  if (rule.timeWindow) {
    const {
      field: timeField,
      startOffset = 0,
      endOffset = 0,
      unit = "day",
      comparator,
    } = rule.timeWindow;

    const baseDate = context[timeField];
    const measureDate = context.dataCollectedDate;

    if (!baseDate?.isValid() || !measureDate?.isValid()) {
      return false;
    }

    const startBoundary = baseDate.clone().add(startOffset, unit);
    const endBoundary = baseDate.clone().add(endOffset, unit);

    switch (comparator) {
      case "within":
        // Check if measureDate is in [startBoundary, endBoundary]
        if (!measureDate.isBetween(startBoundary, endBoundary, null, "[]")) {
          return false;
        }
        break;
      case "before":
        if (!measureDate.isBefore(startBoundary)) {
          return false;
        }
        break;
      case "after":
        if (!measureDate.isAfter(startBoundary)) {
          return false;
        }
        break;
      case "outside":
        if (
          !measureDate.isBefore(startBoundary) &&
          !measureDate.isAfter(endBoundary)
        ) {
          return false;
        }
        break;
      default:
        return false;
    }
  }

  // 2) Evaluate the main operator for rule.field
  let leftVal = null;
  switch (rule.field) {
    case "harvestDate":
      leftVal = context.harvestDate?.valueOf() || null;
      break;
    case "dataCollectedDate":
      leftVal = context.dataCollectedDate?.valueOf() || null;
      break;
    case "plantingDate":
      leftVal = context.plantingDate?.valueOf() || null;
      break;
    case "averagePercentile80_95":
      leftVal = context.averagePercentile80_95;
      break;
    case "averageLength":
      leftVal = context.averageLength;
      break;
    default:
      return false;
  }

  if (leftVal == null) {
    return false;
  }

  const rightVal = rule.value;

  switch (rule.operator) {
    case ">":
      return leftVal > rightVal;
    case "<":
      return leftVal < rightVal;
    case ">=":
      return leftVal >= rightVal;
    case "<=":
      return leftVal <= rightVal;
    case "!=":
      return leftVal !== rightVal;
    case "=":
    case "==":
      return leftVal === rightVal;
    default:
      return false;
  }
}

export function evaluateAllRules(rules, context) {
  if (!rules || rules.length === 0) return false;
  return rules.some((r) => evaluateSingleRule(r, context));
}

export function gatherExcludedDatesForGreenhouse(
  rules,
  greenhouseName,
  rowDataMap,
  greenhouseData,
) {
  const excludedDates = new Set();
  if (!rules || rules.length === 0) return excludedDates;

  for (const [rowName, { tableData, rowRef }] of rowDataMap.entries()) {
    const rowMeasurements = greenhouseData?.[rowName] || [];
    // We might need plantingDate, harvestDate from tableData
    const plantingDate = tableData?.plantingDate
      ? dayjs(tableData.plantingDate)
      : null;
    const harvestDate = tableData?.harvestDate
      ? dayjs(tableData.harvestDate)
      : null;

    // Evaluate each measurement
    for (const entry of rowMeasurements) {
      if (!entry || !Object.keys(entry).length) {
        continue;
      }
      const measureDate = dayjs(entry.date, UI_DATE_FORMAT);
      const avgLength = entry.averageLength || 0;
      let avgP80_95 = entry.averagePercentile80_95 || 0;

      // Build context
      const contextObj = {
        harvestDate,
        plantingDate,
        dataCollectedDate: measureDate,
        averagePercentile80_95: avgP80_95,
        averageLength: avgLength,
      };

      const shouldExclude = evaluateAllRules(rules, contextObj);
      if (shouldExclude) {
        excludedDates.add(
          dayjs(entry.date, UI_DATE_FORMAT).format(BACKEND_DATE_FORMAT),
        );
      }
    }
  }
  return excludedDates;
}
